import moment from 'moment';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import { useEffect, useState } from 'react';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import '../../../scss/Components/_modal.scss';
import 'react-toastify/dist/ReactToastify.css';
import customAxios from '../../../utils/customAxios';
import { GlobalVariables } from '../../../globalVar';
import { getToast } from '../../../utils/toastConfig';
import { useTranslationHook } from '../../../hooks/UseTranslationHook';

const ModalU = (props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPW] = useState('');
  const [verifyPassword, setVPW] = useState('');
  const [role, setRole] = useState('');
  const [entryDate, setEntryDate] = useState(moment());
  const [notes, setNotes] = useState('');
  const [job, setJob] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  const { notesString, jobString, techEntryDateString } = useTranslationHook();

  useEffect(() => {
    if (props.show) {
      fetchClients();
      fetchRoles();
    }
  }, [props.show]);

  const fetchClients = async () => {
    setIsFetching(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}listClients/`;
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 204) {
        setClients([]);
        return;
      }
      setClients(response.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await customAxios.get(
        `${GlobalVariables.getURL()}autofillRoles`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      setRolesData(response.data);
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        title: 'Error al cargar roles',
      });
    }
  };

  const weakPWErrorAlert = (missingRequirements) => {
    toast.dismiss();
    if (missingRequirements.length > 0) {
      toast.error(
        'La contraseña ingresada es débil. No cumple con los siguientes requisitos:',
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        }
      );

      missingRequirements.forEach((requirement) => {
        toast.info(`${requirement}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const hide = () => {
    props.onHide();
    toast.dismiss();
    getToast({
      status: 'success',
      title: 'Usuario creado exitosamente en la base de datos.',
    });
    props.getUsers();
  };

  const createUser = (event) => {
    event.preventDefault();
    setIsLoading(true);
    const userClients = selectedClients.map((client) => client.id);
    if (password === verifyPassword) {
      const data = {
        name: name,
        last_names: lastName,
        email: email,
        password: password,
        roles_id: role,
        entry_date: entryDate,
        job,
        notes,
        clients: userClients,
        regions: [],
      };
      customAxios
        .post(`${GlobalVariables.getURL()}register`, data, {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        })
        .then(
          (response) => {
            console.log(response);
            hide();
          },
          (error) => {
            console.log(error);
            const errorMessage = error.response.data.message;

            if (errorMessage === 'Usuario ya existe.') {
              toast.dismiss();
              getToast({
                status: 'error',
                title:
                  'Ya existe un usuario con esta información. Vuelve a intentarlo.',
              });
            } else if (errorMessage.includes('Licencias insuficientes')) {
              toast.dismiss();
              getToast({
                status: 'error',
                title: 'Licencias insuficientes para el rol Client.',
              });
            } else if (errorMessage.startsWith('Contraseña débil.')) {
              const requirements = [
                'Debe contener al menos una letra mayúscula.',
                'Debe contener al menos un número.',
                'Debe contener al menos un caracter especial (!@#$%^&*).',
                'Debe tener al menos 9 caracteres de longitud.',
              ];

              const missingRequirements = requirements.filter((requirement) =>
                errorMessage.includes(requirement)
              );

              weakPWErrorAlert(missingRequirements);
            } else {
              toast.dismiss();
              getToast({
                status: 'error',
                title: 'Error al crear usuario. Vuelve a intentarlo.',
              });
            }
          }
        )
        .finally(() => setIsLoading(false));
    } else {
      toast.dismiss();
      getToast({
        status: 'error',
        title: 'Contraseñas no coinciden. Vuelve a intentarlo.',
      });
      setIsLoading(false);
    }
  };

  const resetFields = () => {
    setShowPassword(false);
    setRolesData([]);
    setEmail('');
    setName('');
    setLastName('');
    setPW('');
    setVPW('');
    setRole('');
    setEntryDate(moment());
    setNotes('');
    setJob('');
    setIsFetching(false);
    setIsLoading(false);
    setClients([]);
    setSelectedClients([]);
  };

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName="modal-backdrop"
      centered
      onExit={resetFields}
    >
      <Modal.Header closeButton>
        <Modal.Title className="modal-title" id="contained-modal-title-vcenter">
          Nuevo usuario
        </Modal.Title>
      </Modal.Header>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale={moment.locale()}
      >
        <Modal.Body>
          <form id="users-form" onSubmit={createUser}>
            <div className="row">
              <div className="col-md-12">
                <TextField
                  sx={{ width: '100%' }}
                  label="Correo electrónico"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-6">
                <TextField
                  sx={{ width: '100%' }}
                  label="Nombre(s)"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <TextField
                  sx={{ width: '100%' }}
                  label="Apellido(s)"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-6">
                <TextField
                  sx={{ width: '100%' }}
                  label={jobString}
                  type="text"
                  value={job}
                  onChange={(e) => setJob(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <DatePicker
                  sx={{ width: '100%' }}
                  label={techEntryDateString}
                  type="date"
                  value={entryDate}
                  onChange={(newDate) => {
                    setEntryDate(newDate);
                  }}
                />
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-6">
                <Tooltip
                  title="Mínimo 9 caracteres (una mayúscula, un número y un caracter especial)."
                  arrow
                >
                  <TextField
                    sx={{ width: '100%' }}
                    label="Contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPW(e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon />
                            ) : (
                              <VisibilityOutlinedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </div>
              <div className="col-md-6">
                <Tooltip
                  title="Mínimo 9 caracteres (una mayúscula, un número y un caracter especial)."
                  arrow
                >
                  <TextField
                    sx={{ width: '100%' }}
                    label="Repetir contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={verifyPassword}
                    onChange={(e) => setVPW(e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon />
                            ) : (
                              <VisibilityOutlinedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-12">
                {isFetching ? (
                  <Skeleton variant="rounded" height={56} />
                ) : (
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    options={clients}
                    getOptionLabel={(client) => client.business_name}
                    onChange={(e, newValue) => {
                      setSelectedClients(newValue);
                    }}
                    value={selectedClients}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Plazas"
                        placeholder="Plazas..."
                      />
                    )}
                    disableCloseOnSelect
                  />
                )}
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-12">
                <Autocomplete
                  sx={{ width: '100%' }}
                  disablePortal
                  includeInputInList={false}
                  options={rolesData}
                  getOptionLabel={(option) => option.role_name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar rol..."
                      required
                    />
                  )}
                  onChange={(event, newValue) => {
                    setRole(newValue.id);
                  }}
                />
              </div>
            </div>

            <br />

            <div className="row">
              <div className="col-md-12">
                <TextField
                  sx={{ width: '100%' }}
                  label={notesString}
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
      </LocalizationProvider>
      <Modal.Footer>
        <Button
          type="submit"
          form="users-form"
          className="save-modal-button"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size="2rem" />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalU;
